<template>
  <b-row class="digital-report-info-row mt-5">
    <b-col>
      <p>
        {{
          ft(
            featureName,
            'reportingChannels.digitalReport.createDescription',
            $t
          )
        }}
      </p>
    </b-col>
  </b-row>
  <b-row class="digital-report-text-input-row mt-7">
    <b-col class="col-12 col-md-7">
      <VueEditor
        v-model="message"
        :editor-toolbar="customEditorToolbar"
        :editor-options="editorSettings"
        class="mb-3"
        :placeholder="
          ft(featureName, 'reportingChannels.digitalReport.placeholder', $t)
        "
        @focus="onEditorFocus"
      />
    </b-col>
    <b-col class="reporter-guide col-12 col-md-5">
      <div v-if="showReporterGuide">
        <h5>
          {{
            ft(
              featureName,
              'reportingChannels.digitalReport.reporterGuide.title',
              $t
            )
          }}
        </h5>
        <ul>
          <li
            v-for="question in helpQuestions"
            :key="question"
            class="question"
          >
            {{ question }}
          </li>
        </ul>
      </div>
    </b-col>
  </b-row>
  <b-row class="email-check mt-3">
    <b-col class="d-flex align-items-center">
      <puls-form-checkbox
        v-model="checkboxesSelected"
        :options="[
          {
            value: 'email',
            title: ft(featureName, 'create.options.includeEmail', $t)
          }
        ]"
      />
    </b-col>
    <b-col>
      <info-badge :text="ft(featureName, 'create.email.help', $t)" />
    </b-col>
  </b-row>
  <b-row v-if="emailChecked" class="email-row mt-5">
    <b-col class="col-12 col-md-7">
      <puls-form-input
        v-model="email"
        type="email"
        placeholder="name@example.com"
        :label="ft(featureName, 'create.email.label', $t)"
        :is-floating-label="true"
        required
        class="mb-7"
      />
    </b-col>
  </b-row>
  <b-row v-if="!emailChecked" class="no-email-check-row mt-3">
    <b-col class="d-flex align-items-start" cols="8">
      <puls-form-checkbox
        v-model="checkboxesSelected"
        :options="[
          {
            value: 'noEmailConfirm',
            title: ft(
              featureName,
              'create.options.confirmNotToIncludeEmail',
              $t
            )
          }
        ]"
      />
    </b-col>
  </b-row>
</template>
<script>
import { VueEditor, Quill } from 'vue3-editor'
import { ImageDrop } from 'quill-image-drop-module'
import LocalizableComponent from './LocalizableComponent.vue'
import isValidEmailAddress from '../helpers/create-report-form-helper'
import InfoBadge from './InfoBadge.vue'

Quill.register('modules/imageDrop', ImageDrop)

export default {
  name: 'DigitalReportForm',
  components: { InfoBadge, VueEditor },
  extends: LocalizableComponent,
  props: {
    onChange: {
      type: Function,
      default: () => {}
    },
    initialFormData: {
      type: Object,
      default: null
    },
    featureName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      checkboxesSelected: ['email'],
      email: null,
      message: '',
      minLength: 3,
      maxLength: 2000000,
      // customModulesForEditor: [
      // { alias: 'imageDrop', module: ImageDrop }
      // { alias: 'imageResize', module: ImageResize }
      // ],
      editorSettings: {
        modules: {
          imageDrop: true
        }
      },
      showReporterGuide: false,
      customEditorToolbar: [
        // ['bold', 'italic', 'underline'],
        // [{ list: 'ordered' }, { list: 'bullet' }],
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'], // toggled buttons
        ['blockquote'],

        [{ list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }], // outdent/indent

        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        ['clean'],
        ['image']
      ]
    }
  },
  computed: {
    emailChecked() {
      return this.checkboxesSelected.includes('email')
    },
    confirmNoEmailChecked() {
      return this.checkboxesSelected.includes('noEmailConfirm')
    },
    validForm() {
      return this.validReport && this.validEmailSettings
    },
    validReport() {
      return (
        this.message.length > this.minLength &&
        this.message.length < this.maxLength
      )
    },
    validEmailSettings() {
      return this.emailChecked
        ? this.emailChecked && isValidEmailAddress(this.email)
        : this.confirmNoEmailChecked
    },
    helpQuestions() {
      return [
        this.ft(
          this.featureName,
          'reportingChannels.digitalReport.reporterGuide.question1',
          this.$t
        ),
        this.ft(
          this.featureName,
          'reportingChannels.digitalReport.reporterGuide.question2',
          this.$t
        ),
        this.ft(
          this.featureName,
          'reportingChannels.digitalReport.reporterGuide.question3',
          this.$t
        ),
        this.ft(
          this.featureName,
          'reportingChannels.digitalReport.reporterGuide.question4',
          this.$t
        ),
        this.ft(
          this.featureName,
          'reportingChannels.digitalReport.reporterGuide.question5',
          this.$t
        ),
        this.ft(
          this.featureName,
          'reportingChannels.digitalReport.reporterGuide.question6',
          this.$t
        )
      ]
    }
  },
  watch: {
    message() {
      this.handleUpdatedFormData()
    },
    email() {
      this.handleUpdatedFormData()
    },
    validForm() {
      this.handleUpdatedFormData()
    },
    emailChecked(newVal) {
      if (!newVal) {
        // dont want email anymore
        this.email = null
      }
    }
  },
  mounted() {
    this.showReporterGuide = false
    if (this.initialFormData) {
      this.message = this.initialFormData.message || ''
      this.email = this.initialFormData.email
    }
  },
  methods: {
    handleUpdatedFormData() {
      const formData = {
        message: this.message
      }
      if (isValidEmailAddress(this.email)) {
        formData.email = this.email
      } else {
        formData.email = null
      }
      this.onChange({ validForm: this.validForm, formData })
    },
    onEditorFocus() {
      this.showReporterGuide = true
    }
  }
}
</script>
<style scoped>
.reporter-guide {
  font-size: 1.1rem;
}
.guide-title {
  font-weight: 400;
}
.question {
  line-height: 45px;
}
</style>
<style>
.ql-editor {
  height: 35rem !important;
}
.ql-editor.ql-blank:before {
  color: var(--kt-text-gray-500);
}
.quillWrapper .ql-toolbar {
  border-top-left-radius: 0.95rem;
  border-top-right-radius: 0.95rem;
  background-color: var(--kt-input-solid-bg);
  border: 1px solid var(--kt-input-border-color);
}

.quillWrapper .ql-container {
  border-bottom-left-radius: 0.95rem;
  border-bottom-right-radius: 0.95rem;
  background-color: var(--kt-input-bg);
  border: 1px solid var(--kt-input-border-color);
}
.quillWrapper:focus-within .ql-toolbar {
  border-color: var(--kt-input-focus-border-color);
  transition: all 0.2s ease;
}
.quillWrapper:focus-within .ql-container {
  background-color: var(--kt-input-focus-bg);
  border-color: var(--kt-input-focus-border-color);
  transition: all 0.2s ease;
}
</style>
